import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Brain,
  Bot,
  ListTodo,
  Shield,
  CheckCircle,
  Bell,
  MessageSquare,
  Plug,
  Calendar,
  CalendarClock,
  ShoppingCart,
  UserCircle2,
  Server,
  Settings as SettingsIcon,
  Wallet,
  X,
} from 'lucide-react';
import { useApprovals } from '@/hooks/useApprovals';
import { getBackendVersion } from '@/api/version.api';
import clsx from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  badge?: number;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  // N18 §14 #5 (2026-06-07): poll каждые 30 сек чтобы badge показывал
  // актуальный count даже если юзер не заходит в /approvals. Polling
  // паузится автоматически при tab-hidden (см. useApprovals).
  const { pendingCount } = useApprovals({ pollIntervalMs: 30_000 });
  // UP-16 (2026-05-17): backend version из health/version endpoint, поллится один раз при mount.
  const [backendVersion, setBackendVersion] = useState<string>('…');
  useEffect(() => {
    void getBackendVersion().then(setBackendVersion);
  }, []);

  const navItems: NavItem[] = [
    {
      to: '/dashboard',
      labelKey: 'nav.dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      to: '/memory',
      labelKey: 'nav.memory',
      icon: <Brain className="h-5 w-5" />,
    },
    {
      to: '/profile',
      labelKey: 'nav.profile',
      icon: <UserCircle2 className="h-5 w-5" />,
    },
    {
      to: '/agents',
      labelKey: 'nav.agents',
      icon: <Bot className="h-5 w-5" />,
    },
    {
      to: '/tasks',
      labelKey: 'nav.tasks',
      icon: <ListTodo className="h-5 w-5" />,
    },
    {
      to: '/conversations',
      labelKey: 'nav.conversations',
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      to: '/reminders',
      labelKey: 'nav.reminders',
      icon: <Bell className="h-5 w-5" />,
    },
    {
      to: '/calendar',
      labelKey: 'nav.calendar',
      icon: <Calendar className="h-5 w-5" />,
    },
    // NBF-SCHED MVP SCHED-7b (2026-05-30): «Запланированные» — cron-задачи.
    {
      to: '/scheduled-tasks',
      labelKey: 'nav.scheduledTasks',
      icon: <CalendarClock className="h-5 w-5" />,
    },
    // UP-29 / S1 Phase C (2026-05-26): Smart Lists menu item.
    {
      to: '/lists',
      labelKey: 'nav.lists',
      icon: <ShoppingCart className="h-5 w-5" />,
    },
    {
      to: '/approvals',
      labelKey: 'nav.approvals',
      icon: <CheckCircle className="h-5 w-5" />,
      badge: pendingCount,
    },
    {
      to: '/integrations',
      labelKey: 'nav.integrations',
      icon: <Plug className="h-5 w-5" />,
    },
    // S13.1 (2026-05-27): dedicated MCP catalog page —
    // discoverability fix per manual QA «MCP не могу найти в UI».
    {
      to: '/mcp',
      labelKey: 'nav.mcp',
      icon: <Server className="h-5 w-5" />,
    },
    {
      to: '/privacy',
      labelKey: 'nav.privacy',
      icon: <Shield className="h-5 w-5" />,
    },
    // UP-14 Phase 1 PR-3 (2026-05-20): /settings page link.
    {
      to: '/settings',
      labelKey: 'nav.settings',
      icon: <SettingsIcon className="h-5 w-5" />,
    },
    // N16 Phase 2 step 2 (2026-06-07): /subscription pricing + checkout.
    {
      to: '/subscription',
      labelKey: 'nav.subscription',
      icon: <Wallet className="h-5 w-5" />,
    },
  ];

  return (
    <>
      {/* Overlay для мобильной версии */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700',
          // Мобильный: fixed, скрыт по умолчанию
          'fixed left-0 top-0 z-50 -translate-x-full transition-transform duration-200 ease-in-out',
          // Десктоп: !static чтобы переопределить fixed
          'md:!static md:translate-x-0',
          // Показываем когда открыт
          isOpen && '!translate-x-0'
        )}
      >
        {/* Flex контейнер для контента сайдбара */}
        <div className="flex flex-col h-full">
          {/* Заголовок сайдбара (мобильная версия) */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700 md:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">{t('common.brand.mark')}</span>
              </div>
              <span className="font-semibold">{t('common.brand.name')}</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('common.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Навигация */}
          <nav className="p-4 space-y-1 overflow-y-auto flex-1 pb-40">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
              {t('nav.dashboard')}
            </p>

            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  )
                }
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span className="font-medium">{t(item.labelKey)}</span>
                </div>

                {item.badge !== undefined && item.badge > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs text-white font-medium">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Нижняя секция */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
            <div className="card p-3 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/50 dark:to-primary-800/50 border-primary-200 dark:border-primary-700">
              <p className="text-sm font-medium text-primary-800 dark:text-primary-200">
                {t('common.brand.ecosystem')}
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                {t('dashboard.subtitle')}
              </p>
              {/* UP-16 (2026-05-17): версии backend + frontend */}
              <p className="text-[10px] text-primary-500 dark:text-primary-400/70 mt-2 font-mono opacity-70">
                {t('common.versions', {
                  backend: backendVersion,
                  frontend: __FRONTEND_VERSION__,
                })}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
